import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trash2, Ban, FileSignature, Search, MessageCircle, Repeat, Car, Bike } from "lucide-react";
import { toast } from "sonner";

// Build a WhatsApp deep-link with a pre-filled message that includes the
// customer + booking context. Admin clicks → mobile WA opens with the message
// ready to send. Saves the "ciao chi sei? quale prenotazione?" ping-pong.
function buildWhatsAppLink(b, isEN) {
  const phoneDigits = String(b.user_phone || "").replace(/[^0-9]/g, "");
  const name = [b.user_name, b.user_surname].filter(Boolean).join(" ").trim() || b.user_email || "";
  const period = b.start_at
    ? `${new Date(b.start_at).toLocaleDateString()} → ${new Date(b.end_at || b.start_at).toLocaleDateString()}`
    : "";
  const vehicle = [b.scooter_model, b.scooter_plate].filter(Boolean).join(" · ");
  const msg = isEN
    ? `Hi ${name}, this is Rent Lupi — about your booking #${(b.id || "").slice(0, 8)} (${vehicle}, ${period}):`
    : `Ciao ${name}, sono Rent Lupi — riguardo la tua prenotazione #${(b.id || "").slice(0, 8)} (${vehicle}, ${period}):`;
  return `https://wa.me/${phoneDigits}?text=${encodeURIComponent(msg)}`;
}

export default function AdminBookings() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const [filter, setFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Iter 53 — vehicle swap dialog. `swapBooking` is the booking being changed,
  // `swapCandidates` is the list of vehicles the backend reports free for the
  // whole booking period. `swapLoading` prevents double-clicks while we POST.
  const [swapBooking, setSwapBooking] = useState(null);
  const [swapCandidates, setSwapCandidates] = useState(null); // null = still fetching
  const [swapLoading, setSwapLoading] = useState(false);

  const STATUS = {
    pagato: { label: t("admin.filter_paid"), className: "bg-[#22c55e]/15 text-[#22c55e]" },
    in_attesa: { label: t("admin.filter_pending"), className: "bg-[#FFD700]/15 text-[#FFD700]" },
    cancellato: { label: t("admin.filter_cancelled"), className: "bg-zinc-700/40 text-zinc-400" },
    admin_block: { label: isEN ? "At-desk block" : "Blocco banco", className: "bg-orange-500/15 text-orange-300" },
  };

  useEffect(() => {
    let active = true;
    const params = filter === "all" ? {} : { payment_status: filter };
    if (search) params.q = search;
    api.get("/bookings", { params })
      .then((r) => { if (active) { setBookings(r.data); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filter, reloadKey, search]);

  // Debounce free-text search by 350ms so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const cancelBooking = async (b) => {
    const msg = isEN
      ? `Cancel this booking by ${b.user_email || "?"}? The customer will see it as 'cancelled' for 7 days, then it will be removed automatically.`
      : `Annullare la prenotazione di ${b.user_email || "?"}? Il cliente la vedrà come "annullata" per 7 giorni, poi verrà rimossa automaticamente.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      toast.success(isEN ? "Booking cancelled" : "Prenotazione annullata");
      setReloadKey((k) => k + 1);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.statusText || e.message;
      toast.error(`${isEN ? "Cannot cancel" : "Impossibile annullare"}: ${formatApiError(detail) || detail}`);
    }
  };

  const deleteBooking = async (b) => {
    const msg = isEN
      ? `Permanently delete this entry? This cannot be undone.`
      : `Eliminare definitivamente? L'operazione è irreversibile.`;
    if (!window.confirm(msg)) return;
    try {
      const url = b.payment_status === "admin_block"
        ? `/bookings/admin/manual-block/${b.id}`
        : `/bookings/admin/${b.id}`;
      await api.delete(url);
      toast.success(isEN ? "Deleted" : "Eliminato");
      setReloadKey((k) => k + 1);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.statusText || e.message;
      toast.error(`${isEN ? "Cannot delete" : "Impossibile eliminare"}: ${formatApiError(detail) || detail}`);
    }
  };

  // Iter 53 — open the "cambia veicolo" dialog and fetch the available fleet
  // for this booking's period. The dialog stays open showing a spinner until
  // we have the list, then renders one row per candidate.
  const openSwap = async (b) => {
    setSwapBooking(b);
    setSwapCandidates(null);
    try {
      const r = await api.get(`/bookings/admin/${b.id}/available-scooters`);
      setSwapCandidates(r.data?.available || []);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`${isEN ? "Cannot load fleet" : "Impossibile caricare la flotta"}: ${formatApiError(detail) || detail}`);
      setSwapCandidates([]);
    }
  };

  const closeSwap = () => {
    setSwapBooking(null);
    setSwapCandidates(null);
    setSwapLoading(false);
  };

  const doSwap = async (candidateId, candidateLabel) => {
    if (!swapBooking) return;
    const msg = isEN
      ? `Swap the vehicle of this booking to "${candidateLabel}"? Price and dates stay the same.`
      : `Sostituire il veicolo di questa prenotazione con "${candidateLabel}"? Prezzo e date restano invariati.`;
    if (!window.confirm(msg)) return;
    setSwapLoading(true);
    try {
      const r = await api.post(`/bookings/admin/${swapBooking.id}/change-scooter`, { new_scooter_id: candidateId });
      toast.success(
        (isEN ? "Vehicle changed → " : "Veicolo cambiato → ") +
          `${r.data?.new_model} (${r.data?.new_plate})`
      );
      closeSwap();
      setReloadKey((k) => k + 1);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`${isEN ? "Swap failed" : "Cambio non riuscito"}: ${formatApiError(detail) || detail}`);
      setSwapLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">{t("admin.bookings_tag")}</p>
        <h1 className="font-display text-4xl font-bold tracking-tight">{t("admin.bookings_title")}</h1>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="all" data-testid="filter-all" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">{t("admin.filter_all")}</TabsTrigger>
          <TabsTrigger value="pagato" data-testid="filter-pagato" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">{t("admin.filter_paid")}</TabsTrigger>
          <TabsTrigger value="in_attesa" data-testid="filter-in-attesa" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">{t("admin.filter_pending")}</TabsTrigger>
          <TabsTrigger value="cancellato" data-testid="filter-cancellato" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">{t("admin.filter_cancelled")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search bar — find a booking by customer name/surname/email, plate, or model.
          Useful e.g. when a traffic fine arrives at the address and you have only the plate. */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={isEN ? "Search by name, surname, plate, model…" : "Cerca per nome, cognome, targa, modello…"}
          className="bg-zinc-900 border-zinc-800 pl-9 focus:ring-[#FFD700]/50"
          data-testid="bookings-search-input"
        />
        {search && (
          <p className="text-[11px] text-zinc-500 mt-1">
            {isEN ? `Showing results for "${search}"` : `Risultati per "${search}"`} — {bookings.length}
          </p>
        )}
      </div>

      <div className="border border-zinc-800 rounded-md overflow-hidden">
        <Table>          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.date")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.customer")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("checkout.scooter")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.period")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("checkout.total")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.status")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">{t("admin.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-zinc-500 text-center py-8">{t("common.loading")}</TableCell></TableRow>
            ) : bookings.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-zinc-500 text-center py-8">{t("admin.no_bookings")}</TableCell></TableRow>
            ) : bookings.map((b) => {
              const st = STATUS[b.payment_status] || { label: b.payment_status || "?", className: "bg-zinc-700/40 text-zinc-300" };
              const totalAmt = Number(b.total_amount || 0);
              return (
                <TableRow key={b.id} className="border-zinc-800" data-testid="booking-row">
                  <TableCell className="text-zinc-400 text-sm">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-sm">{b.user_email || (b.manual_customer_name ? `${b.manual_customer_name} (banco)` : "—")}</TableCell>
                  <TableCell><div className="font-medium">{b.scooter_model || "—"}</div><div className="text-xs text-zinc-500 uppercase">{b.scooter_plate || ""}</div></TableCell>
                  <TableCell className="text-sm text-zinc-400">
                    {b.start_at ? new Date(b.start_at).toLocaleDateString() : "—"} → {b.end_at ? new Date(b.end_at).toLocaleDateString() : "—"}
                    <div className="text-xs text-zinc-500">{b.hours || 0} {t("rentals.hours_unit")}</div>
                  </TableCell>
                  <TableCell className="text-[#FFD700] font-semibold">€{totalAmt.toFixed(2)}</TableCell>
                  <TableCell><Badge className={`${st.className} border-0`}>{st.label}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1 items-center justify-end">
                      {b.user_phone && (
                        <Button asChild size="sm" variant="ghost"
                                className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-8 px-2"
                                title={isEN ? "Open WhatsApp chat with this customer" : "Apri chat WhatsApp con il cliente"}
                                data-testid={`booking-whatsapp-${b.id}`}>
                          <a target="_blank" rel="noopener noreferrer"
                             href={buildWhatsAppLink(b, isEN)}>
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      {(b.payment_status === "pagato" || b.payment_status === "admin_block") && b.scooter_id && !b.converted_to_contract_id && (
                        <Button size="sm" variant="ghost" onClick={() => openSwap(b)}
                                className="text-sky-300 hover:text-sky-200 hover:bg-sky-500/10 h-8 px-2 gap-1"
                                title={isEN ? "Swap the assigned vehicle (dates and price stay the same)" : "Cambia il veicolo assegnato (date e prezzo invariati)"}
                                data-testid={`booking-change-scooter-${b.id}`}>
                          <Repeat className="w-4 h-4" />
                          <span className="hidden md:inline text-xs">{isEN ? "Swap" : "Cambia"}</span>
                        </Button>
                      )}
                      {(b.payment_status === "pagato" || b.payment_status === "admin_block") && b.scooter_id && !b.converted_to_contract_id && (
                        <Button asChild size="sm" variant="ghost"
                                className="text-[#FFD700] hover:text-[#FACC15] hover:bg-[#FFD700]/10 h-8 px-2 gap-1"
                                title={
                                  isEN
                                    ? (b.payment_status === "admin_block" ? "Create contract from walk-in block" : "Generate printable contract")
                                    : (b.payment_status === "admin_block" ? "Crea contratto da blocco walk-in" : "Genera contratto cartaceo")
                                }
                                data-testid={`booking-make-contract-${b.id}`}>
                          <Link to={`/admin/contracts/new?from_booking=${b.id}`}>
                            <FileSignature className="w-4 h-4" />
                            <span className="hidden md:inline text-xs">{isEN ? "Contract" : "Contratto"}</span>
                          </Link>
                        </Button>
                      )}
                      {b.converted_to_contract_id && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400 px-2"
                              title={isEN ? "Already converted to a printed contract" : "Già convertita in contratto cartaceo"}
                              data-testid={`booking-converted-${b.id}`}>
                          <FileSignature className="w-3.5 h-3.5" />
                          {isEN ? "Converted" : "Convertita"}
                        </span>
                      )}
                      {b.payment_status !== "cancellato" && b.payment_status !== "admin_block" && (
                        <Button size="sm" variant="ghost" onClick={() => cancelBooking(b)}
                                className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 h-8 px-2 gap-1"
                                title={isEN ? "Cancel (customer sees it for 7 days)" : "Annulla (visibile al cliente per 7 giorni)"}
                                data-testid={`booking-cancel-${b.id}`}>
                          <Ban className="w-4 h-4" />
                          <span className="hidden md:inline text-xs">{isEN ? "Cancel" : "Annulla"}</span>
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteBooking(b)}
                              className="text-zinc-500 hover:text-red-400 hover:bg-zinc-800 h-8 px-2"
                              title={isEN ? "Permanently delete" : "Elimina definitivamente"}
                              data-testid={`booking-delete-${b.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Iter 53 — Vehicle swap dialog. Only shows candidates the backend
          confirmed are free for the whole booking period. Same-type vehicles
          come first (server-sorted) so the natural replacement is on top. */}
      <Dialog open={!!swapBooking} onOpenChange={(open) => { if (!open) closeSwap(); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="swap-scooter-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FFD700]">
              {isEN ? "Swap the assigned vehicle" : "Cambia veicolo assegnato"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {swapBooking && (
                <>
                  <span className="block">
                    {isEN ? "Booking" : "Prenotazione"} <span className="text-zinc-200">#{(swapBooking.id || "").slice(0, 8)}</span>
                    {" · "}
                    <span className="text-zinc-200">{swapBooking.scooter_model} ({swapBooking.scooter_plate})</span>
                  </span>
                  <span className="block text-xs mt-1">
                    {isEN ? "Period:" : "Periodo:"}{" "}
                    {swapBooking.start_at ? new Date(swapBooking.start_at).toLocaleDateString() : "—"} →{" "}
                    {swapBooking.end_at ? new Date(swapBooking.end_at).toLocaleDateString() : "—"}
                  </span>
                  <span className="block text-[11px] mt-2 text-amber-300">
                    {isEN
                      ? "Only vehicles free for the whole period are listed. Price stays the same."
                      : "Sono elencati solo i veicoli liberi per tutto il periodo. Il prezzo non cambia."}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[50vh] overflow-y-auto space-y-2" data-testid="swap-candidates-list">
            {swapCandidates === null ? (
              <div className="text-center text-zinc-500 py-6 text-sm">{t("common.loading")}</div>
            ) : swapCandidates.length === 0 ? (
              <div className="text-center text-zinc-500 py-6 text-sm">
                {isEN ? "No other vehicles are free for this period." : "Nessun altro veicolo libero in questo periodo."}
              </div>
            ) : swapCandidates.map((v) => {
              const Icon = v.vehicle_type === "auto" ? Car : Bike;
              return (
                <button
                  key={v.id}
                  disabled={swapLoading}
                  onClick={() => doSwap(v.id, `${v.model} (${v.plate})`)}
                  data-testid={`swap-candidate-${v.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-sky-500/50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon className="w-5 h-5 text-sky-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-100 truncate">{v.model || "—"}</div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">
                      {v.plate || "—"}
                      {v.displacement ? ` · ${v.displacement}cc` : ""}
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-500 shrink-0">
                    {v.vehicle_type === "auto" ? (isEN ? "Car" : "Auto") : "Scooter"}
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
